import { useState } from 'react';

import {
  Box,
  Link,
  Card,
  Step,
  Stack,
  Alert,
  Paper,
  Stepper,
  Checkbox,
  StepLabel,
  Typography,
  CardContent,
  StepContent,
  FormControlLabel,
} from '@mui/material';

import { useTranslate } from 'src/locales';
import { ChannelStepLayout } from 'src/pages/channel/shared';

import { Iconify } from 'src/components/iconify';

export default function LineStep2() {
  const [checked, setChecked] = useState(false);
  const { t } = useTranslate('translation');

  const steps = [
    {
      label: t('LINE_STEP2_LOGIN'),
      description: (
        <>
          {t('LINE_STEP2_LOGIN')}{' '}
          <Link
            href="https://manager.line.biz/"
            target="_blank"
            sx={{
              fontWeight: 600,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {t('LINE_OFFICIAL_ACCOUNTS_PLATFORM')}
            <Iconify icon="mdi:open-in-new" width={16} height={16} />
          </Link>
        </>
      ),
    },
    {
      label: t('LINE_STEP2_ENABLE_MESSAGING_API'),
      description: t('LINE_STEP2_ENABLE_MESSAGING_API'),
    },
    {
      label: t('LINE_STEP2_CREATE_DEV_ACCOUNT'),
      description: t('LINE_STEP2_CREATE_DEV_ACCOUNT'),
    },
    {
      label: t('LINE_STEP2_ADD_PROVIDER'),
      description: t('LINE_STEP2_ADD_PROVIDER'),
    },
    {
      label: t('LINE_STEP2_ADD_PRIVACY_POLICY'),
      description: t('LINE_STEP2_ADD_PRIVACY_POLICY'),
    },
    {
      label: t('LINE_STEP2_CLICK_OK'),
      description: t('LINE_STEP2_CLICK_OK'),
    },
    {
      label: t('LINE_STEP2_ENABLE_WEBHOOK'),
      description: t('LINE_STEP2_ENABLE_WEBHOOK'),
    },
  ];

  return (
    <Stack spacing={4} sx={{ height: '100%' }}>
      <ChannelStepLayout
        title={t('LINE_CHANNEL_SETUP')}
        subtitle={t('LINE_SETUP_GUIDE_DESCRIPTION')}
        isNextDisabled={!checked}
      >
        <Card
          sx={{
            boxShadow: (theme1) => theme1.shadows[4],
            border: (theme1) => `1px solid ${theme1.palette.divider}`,
            flexGrow: 1,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Stack spacing={3}>
              {/* Info Alert */}
              <Alert
                severity="info"
                icon={<Iconify icon="mdi:information" />}
                sx={{
                  border: (theme1) => `1px solid ${theme1.palette.info.light}`,
                  backgroundColor: (theme1) => theme1.palette.info.lighter,
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {t('SETUP_REQUIREMENTS')}
                </Typography>
                <Typography variant="body2">{t('LINE_SETUP_REQUIREMENTS_DESC')}</Typography>
              </Alert>

              {/* Stepper */}
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  backgroundColor: 'background.default',
                  borderColor: 'divider',
                }}
              >
                <Stepper orientation="vertical" nonLinear>
                  {steps.map((step, index) => (
                    <Step key={index} active completed={false}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              backgroundColor: 'primary.main',
                              color: 'common.white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold',
                              fontSize: '0.875rem',
                            }}
                          >
                            {index + 1}
                          </Box>
                        )}
                        sx={{
                          '& .MuiStepLabel-label': {
                            fontWeight: 600,
                            fontSize: '1rem',
                          },
                        }}
                      >
                        {step.label}
                      </StepLabel>
                      <StepContent>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 1, lineHeight: 1.6 }}
                        >
                          {step.description}
                        </Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Paper>

              {/* Confirmation Checkbox */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  p: 2,
                  border: (theme1) =>
                    `1px solid ${checked ? theme1.palette.primary.main : theme1.palette.divider}`,
                  borderRadius: 1,
                  backgroundColor: (theme1) =>
                    checked ? theme1.palette.primary.dark : 'transparent',
                  transition: 'all 0.2s ease',
                  color: (theme1) => (checked ? theme1.palette.primary.contrastText : 'inherit'),
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checked}
                      onChange={(e) => setChecked(e.target.checked)}
                      icon={<Iconify icon="mdi:checkbox-blank-outline" />}
                      checkedIcon={<Iconify icon="mdi:check-circle" />}
                      sx={{
                        color: (theme1) =>
                          checked ? theme1.palette.common.white : theme1.palette.text.secondary,
                        '&.Mui-checked': {
                          color: (theme1) => theme1.palette.common.white,
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="600"
                        sx={{
                          color: (theme1) => (checked ? theme1.palette.common.white : 'inherit'),
                        }}
                      >
                        {t('CONFIRM_SETUP_COMPLETION')}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: (theme1) =>
                            checked ? theme1.palette.common.white : 'text.secondary',
                        }}
                      >
                        {t('LINE_CONFIRM_SETUP_DESC')}
                      </Typography>
                    </Box>
                  }
                  sx={{
                    alignItems: 'flex-start',
                    m: 0,
                  }}
                />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </ChannelStepLayout>
    </Stack>
  );
}
